/**
 * Invariantes de plausibilidade de rota (módulo puro, sem rede).
 *
 * Uma violação NUNCA deve ser apresentada como "não encontrei nada": é uma
 * falha técnica e obriga a devolver o crédito/limite reservado.
 */

import { matchCountry } from "./geo-scope";

export type SanityMode = "walk" | "bike" | "scooter" | "car" | "transit" | "train" | "unknown";

export type RouteSegment = {
  distanceMeters: number;
  durationSeconds: number;
  mode?: SanityMode | string;
  fromLabel?: string;
  toLabel?: string;
};

export type SanityIssue = {
  code:
    | "non_finite_value"
    | "negative_value"
    | "implausible_speed"
    | "implausible_duration"
    | "day_overflow"
    | "country_as_stop";
  detail: string;
};

/** Envelopes de velocidade média plausível (km/h). */
const SPEED_ENVELOPE: Record<SanityMode, { min: number; max: number }> = {
  walk: { min: 1.5, max: 8 },
  bike: { min: 4, max: 35 },
  scooter: { min: 4, max: 45 },
  car: { min: 5, max: 130 },
  transit: { min: 3, max: 200 },
  train: { min: 10, max: 320 },
  unknown: { min: 1, max: 320 },
};

const normalizeMode = (mode?: string): SanityMode => {
  const key = (mode ?? "unknown").toLowerCase();
  return (key in SPEED_ENVELOPE ? key : "unknown") as SanityMode;
};

/** Segmentos muito curtos podem ter durações "estranhas" legítimas. */
const SHORT_SEGMENT_METERS = 300;
const SHORT_SEGMENT_MAX_SECONDS: Record<SanityMode, number> = {
  walk: 20 * 60,
  bike: 20 * 60,
  scooter: 20 * 60,
  car: 30 * 60,
  transit: 45 * 60,
  train: 45 * 60,
  unknown: 30 * 60,
};
/** Nenhum troço isolado de um roteiro deve passar de 12 horas. */
const MAX_SEGMENT_SECONDS = 12 * 3600;

export function checkSegment(segment: RouteSegment): SanityIssue[] {
  const issues: SanityIssue[] = [];
  const { distanceMeters: d, durationSeconds: s } = segment;
  const label = `${segment.fromLabel ?? "?"} → ${segment.toLabel ?? "?"}`;

  if (!Number.isFinite(d) || !Number.isFinite(s)) {
    return [{ code: "non_finite_value", detail: `${label}: distância/duração inválidas` }];
  }
  if (d < 0 || s < 0) {
    return [{ code: "negative_value", detail: `${label}: valores negativos` }];
  }
  if (s > MAX_SEGMENT_SECONDS) {
    issues.push({
      code: "implausible_duration",
      detail: `${label}: ${(s / 3600).toFixed(1)}h para ${(d / 1000).toFixed(1)} km`,
    });
  }
  if (d <= SHORT_SEGMENT_METERS) {
    if (s > SHORT_SEGMENT_MAX_SECONDS[normalizeMode(segment.mode)]) {
      issues.push({
        code: "implausible_duration",
        detail: `${label}: ${(s / 60).toFixed(0)} min para apenas ${d.toFixed(0)} m`,
      });
    }
    return issues;
  }
  if (s <= 0) {
    issues.push({ code: "implausible_duration", detail: `${label}: ${(d / 1000).toFixed(1)} km em 0 s` });
    return issues;
  }

  const envelope = SPEED_ENVELOPE[normalizeMode(segment.mode)];
  const kmh = d / 1000 / (s / 3600);
  if (kmh < envelope.min || kmh > envelope.max) {
    issues.push({
      code: "implausible_speed",
      detail: `${label}: ${kmh.toFixed(1)} km/h fora do intervalo ${envelope.min}-${envelope.max} km/h`,
    });
  }
  return issues;
}

export type DayPlanCheck = {
  segments: RouteSegment[];
  /** Horas úteis disponíveis nesse dia. */
  activeHours?: number;
  /** Nomes das paragens (para garantir que nenhuma é um país). */
  stopLabels?: string[];
};

export function checkDayPlan(day: DayPlanCheck): SanityIssue[] {
  const issues = day.segments.flatMap(checkSegment);

  for (const label of day.stopLabels ?? []) {
    if (matchCountry(label)) {
      issues.push({ code: "country_as_stop", detail: `"${label}" é um país, não uma paragem` });
    }
  }

  if (day.activeHours && day.activeHours > 0) {
    const travelSeconds = day.segments.reduce(
      (sum, s) => sum + (Number.isFinite(s.durationSeconds) ? Math.max(0, s.durationSeconds) : 0),
      0,
    );
    // Deslocação não pode ultrapassar o dia inteiro disponível.
    if (travelSeconds > day.activeHours * 3600) {
      issues.push({
        code: "day_overflow",
        detail: `${(travelSeconds / 3600).toFixed(1)}h de deslocação para um dia de ${day.activeHours}h`,
      });
    }
  }

  return issues;
}

export function assertRouteSanity(day: DayPlanCheck): void {
  const issues = checkDayPlan(day);
  if (issues.length) {
    throw new Error(`route_sanity_failed:${issues.map((i) => i.code).join(",")}`);
  }
}

/** Formata as horas de deslocação de forma legível ("2h30", "45 min"). */
export function formatTravelDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return "0 min";
  const total = Math.round(seconds / 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h ? `${h}h${m ? String(m).padStart(2, "0") : ""}` : `${m} min`;
}
